import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_GROUPITEM_LOAD_BY_ID,
    ACTION_GROUPITEM_LOAD_FAILURE,
    ACTION_GROUPITEM_LOAD_SUCCESS,

    ACTION_GROUPITEM_SAVE,
    ACTION_GROUPITEM_SAVE_SUCCESS,
    ACTION_GROUPITEM_SAVE_FAILURE,

    ACTION_GROUPITEM_CREATE,
    ACTION_GROUPITEM_CREATE_SUCCESS,
    ACTION_GROUPITEM_CREATE_FAILURE,

    ACTION_GROUPITEM_CLEAR,
    GroupItemAction
} from '../actions/groupItem';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IGroupItemState {
    formData: Dtos.GroupItem | Dtos.GroupItem[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
}

const initialState: IGroupItemState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState
};


function loading(state: IGroupItemState, isLoading: boolean): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IGroupItemState,
    formData: Dtos.GroupItem | Dtos.GroupItem[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function loadFailure(state: IGroupItemState, responseStatus: Dtos.ResponseStatus): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IGroupItemState, propertiesToClear?: string[]): IGroupItemState {

    if (!propertiesToClear) {
        const newState: IGroupItemState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IGroupItemState, isSaving: boolean): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IGroupItemState,
    formData: Dtos.GroupItem | Dtos.GroupItem[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IGroupItemState, responseStatus: Dtos.ResponseStatus): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IGroupItemState, isCreating: boolean): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IGroupItemState,
    formData: Dtos.GroupItem,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IGroupItemState, responseStatus: Dtos.ResponseStatus): IGroupItemState {
    const newState: IGroupItemState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const GroupItemReducer: Reducer<IGroupItemState> = (state: IGroupItemState = initialState, action: GroupItemAction) => {
    switch (action.type) {
        case ACTION_GROUPITEM_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_GROUPITEM_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_GROUPITEM_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_GROUPITEM_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_GROUPITEM_SAVE:
            return saving(state, true);
        case ACTION_GROUPITEM_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_GROUPITEM_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_GROUPITEM_CREATE:
            return creating(state, true);
        case ACTION_GROUPITEM_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_GROUPITEM_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
    }

    return state;
}

export default GroupItemReducer;